import Hls from "hls.js";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { StyleSheet, View } from "react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import { unstable_createElement } from "react-native-web";
import {
  PlayerVideoHandle,
  PlayerVideoSurfaceProps,
} from "./playerVideoTypes";
import type { SubtitleTrackDescriptor } from "../player/subtitleSource";
import {
  applySubtitleOffsetToWebVtt,
  getSubtitleTimeOffsetSec,
  getVttCueOffsetMs,
} from "../player/subtitleFormat";
import {
  decideHlsRecovery,
  HLS_PLAYLIST_TTFB_TIMEOUT_MS,
  shouldDeferHlsFailure,
  type HlsRecoveryDecision,
} from "../player/hlsRecovery";
import { getInitialBufferedPlayheadCorrection } from "../player/hlsTimeline";
import { getSubtitleCandidateFileName } from "../player/subtitleSource";

type WebVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
};

function hasServerStartOffset(uri: string) {
  const query = uri.split("?", 2)[1];
  if (!query) return false;
  return query.split("&").some((part) => {
    const [rawKey, rawValue = ""] = part.split("=", 2);
    try {
      return decodeURIComponent(rawKey).toLowerCase() === "starttimeticks" && Number(decodeURIComponent(rawValue)) > 0;
    } catch {
      return rawKey.toLowerCase() === "starttimeticks" && Number(rawValue) > 0;
    }
  });
}

const REMUX_PLAYBACK_START_TICKS_DATA_ID = "com.remux.playback-start-ticks";

/**
 * Server-acknowledged actual stream start (seconds) from the master
 * playlist's EXT-X-SESSION-DATA. Remux keyframe-aligns copy-mode
 * StartTimeTicks seeks, so the stream can begin up to a GOP earlier than
 * requested; the acknowledged value is the decoder clock's true absolute
 * origin. Returns null when the server did not acknowledge a start offset.
 */
function getConfirmedStreamStartSeconds(sessionData: unknown): number | null {
  if (!sessionData || typeof sessionData !== "object") return null;
  const entry = (sessionData as Record<string, { VALUE?: unknown }>)[
    REMUX_PLAYBACK_START_TICKS_DATA_ID
  ];
  const ticks = Number(entry?.VALUE);
  return Number.isFinite(ticks) && ticks > 0 ? ticks / 10_000_000 : null;
}

// ---- subtitle renderer handles ----

type RendererHandle =
  | { kind: "jassub"; destroy: () => Promise<void>; }
  | { kind: "libbitsub"; destroy: () => Promise<void>; };

const SUBTITLE_FETCH_RETRY_DELAYS_MS = [0, 5_000, 10_000, 15_000, 30_000, 60_000] as const;
const RETRYABLE_SUBTITLE_STATUSES = new Set([429, 502, 503, 504, 524]);

async function fetchSubtitleCandidate(url: string, signal: AbortSignal) {
  let lastError: unknown;
  for (let attempt = 0; attempt < SUBTITLE_FETCH_RETRY_DELAYS_MS.length; attempt += 1) {
    const delayMs = SUBTITLE_FETCH_RETRY_DELAYS_MS[attempt];
    if (delayMs > 0) await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    if (signal.aborted) throw new DOMException("Subtitle request aborted", "AbortError");
    try {
      const response = await fetch(url, { signal, credentials: "omit" });
      if (response.ok || !RETRYABLE_SUBTITLE_STATUSES.has(response.status)) return response;
      lastError = new Error(`Subtitle source is preparing (${response.status})`);
    } catch (error) {
      if (signal.aborted) throw error;
      lastError = error;
    }
  }
  throw lastError ?? new Error("Subtitle source is unavailable");
}

async function destroyRenderer(handle: RendererHandle | null) {
  if (!handle) return;
  try { await handle.destroy(); } catch { /* best-effort */ }
}

export const PlayerVideoSurface = forwardRef<
  PlayerVideoHandle,
  PlayerVideoSurfaceProps
>(function PlayerVideoSurface(
  {
    uri,
    headers,
    posterSource,
    paused,
    muted,
    volume,
    streamStartSeconds,
    livePlayback = false,
    controlsVisible,
    controlsHeight,
    subtitleTracks,
    activeSubtitleIndex,
    rate,
    onReady,
    onPlayingChange,
    onProgress,
    onTransportMetric,
    onBuffering,
    onEnded,
    onError,
  },
  ref,
) {
  const viewport = useReferenceWindowDimensions();
  const videoRef = useRef<WebVideo | null>(null);
  const metadataReadyRef = useRef(false);
  const playBlockedRef = useRef(false);
  const pausedIntentRef = useRef(paused);
  pausedIntentRef.current = paused;
  const hlsRef = useRef<Hls | null>(null);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onTransportMetricRef = useRef(onTransportMetric);
  onTransportMetricRef.current = onTransportMetric;

  // ---- subtitle state ----
  const managedTrackRef = useRef<HTMLTrackElement | null>(null);
  const managedBlobUrlRef = useRef<string | null>(null);
  const managedVttOffsetMsRef = useRef(0);
  const managedVttCueTimesRef = useRef(new WeakMap<TextTrackCue, { start: number; end: number }>());
  const rendererRef = useRef<RendererHandle | null>(null);
  const subtitleAbortRef = useRef<AbortController | null>(null);
  const activeSubSigRef = useRef<string>("");
  const subtitlePayloadCacheRef = useRef(new Map<string, Blob>());
  const firstFragStartRef = useRef<number | null>(null);
  const streamStartSecondsRef = useRef(streamStartSeconds);
  streamStartSecondsRef.current = streamStartSeconds;
  const [fragEpoch, setFragEpoch] = useState(0);
  const headerSignature = JSON.stringify(headers ?? {});
  const posterUri =
    posterSource && typeof posterSource === "object" && "uri" in posterSource
      ? posterSource.uri
      : undefined;
  const [aspectRatio, setAspectRatio] = useState(16 / 9);
  const fittedWidth = Math.min(viewport.width, viewport.height * aspectRatio);
  const fittedHeight = fittedWidth / aspectRatio;
  const handlePlayRejection = (error: unknown) => {
    const errorName = typeof error === "object" && error !== null && "name" in error
      ? String(error.name)
      : "";
    const policyBlocked = errorName === "NotAllowedError" || errorName === "SecurityError";
    // HLS attachment can reject play() with NotSupportedError or AbortError
    // before metadata exists. Those are preparation races, not user intent.
    // A browser policy rejection is authoritative at any time; every other
    // rejection becomes authoritative once a playable decoder exists.
    if (!metadataReadyRef.current && !policyBlocked) return;
    playBlockedRef.current = true;
    onPlayingChange(false);
  };

  useImperativeHandle(ref, () => ({
    seek: (seconds) => {
      if (!videoRef.current || !Number.isFinite(seconds)) return;
      const target = Math.max(0, seconds);
      const duration = videoRef.current.duration;
      const maximum = Number.isFinite(duration) && duration > 0 ? duration : Number.MAX_SAFE_INTEGER;
      videoRef.current.currentTime = Math.min(maximum, target);
    },
    presentFullscreen: () => {
      const doc = document.documentElement;
      if (doc.requestFullscreen) {
        void doc.requestFullscreen().catch(() => {
          videoRef.current?.requestFullscreen?.();
        });
      } else {
        videoRef.current?.webkitEnterFullscreen?.();
      }
    },
    dismissFullscreen: () => {
      if (document.fullscreenElement) void document.exitFullscreen();
      else videoRef.current?.webkitExitFullscreen?.();
    },
    presentPictureInPicture: () => {
      const video = videoRef.current;
      if (!video || !video.ownerDocument.pictureInPictureEnabled) return;
      if (video.ownerDocument.pictureInPictureElement) {
        void video.ownerDocument.exitPictureInPicture();
      } else if (video.requestPictureInPicture) {
        void video.requestPictureInPicture();
      }
    },
  }));

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !uri) return;
    metadataReadyRef.current = false;
    playBlockedRef.current = false;
    firstFragStartRef.current = null;
    setFragEpoch((value) => value + 1);
    let hls: Hls | null = null;
    let cleanupDeferredRecovery = () => {};
    if (/\.m3u8(?:\?|$)/i.test(uri) && Hls.isSupported()) {
      let mediaRecoveryAttempted = false;
      let stallRecoveryAttempted = false;
      let terminalErrorSent = false;
      let initialBufferedAlignmentDone = false;
      let deferredFailure: {
        decision: HlsRecoveryDecision;
        details: string;
        httpStatus?: number;
      } | null = null;
      hls = new Hls({
        // Keep the blob worker disabled: ReNative's Web transform currently
        // injects Webpack helpers into it and makes the worker fail at startup.
        enableWorker: false,
        // Prefetch the first fragment for every browser session. This only
        // overlaps the initial request with media attachment; it does not add
        // startup delay or alter the selected stream quality.
        startFragPrefetch: true,
        startPosition: hasServerStartOffset(uri) ? 0 : -1,
        // Public Jellyfin Live TV streams are ordinary sliding HLS playlists,
        // not low-latency HLS. Start with deliberate runway instead of chasing
        // the newest fragment, then bound how far a prolonged stall may drift.
        // Keep this channel-only: VOD startup and managed Remux HLS retain
        // their existing latency and recovery behavior.
        ...(livePlayback
          ? {
              lowLatencyMode: false,
              initialLiveManifestSize: 3,
              liveSyncDurationCount: 4,
              liveMaxLatencyDurationCount: 8,
              maxLiveSyncPlaybackRate: 1,
            }
          : {}),
        // Remote-backed transcodes are bursty. Keep enough forward media to
        // ride through a source slowdown and retain several minutes behind the
        // playhead so a pause or short recovery does not aggressively evict
        // media the browser has already downloaded.
        maxBufferLength: 60,
        maxMaxBufferLength: 180,
        backBufferLength: 300,
        maxBufferSize: 128 * 1024 * 1024,
        maxBufferHole: 0.5,
        capLevelToPlayerSize: true,
        renderTextTracksNatively: false,
        highBufferWatchdogPeriod: 2,
        nudgeOffset: 0.2,
        nudgeMaxRetry: 5,
        nudgeOnVideoHole: true,
        detectStallWithCurrentTimeMs: 6000,
        manifestLoadPolicy: {
          default: {
            maxTimeToFirstByteMs: 10_000,
            maxLoadTimeMs: 20_000,
            timeoutRetry: {
              maxNumRetry: 2,
              retryDelayMs: 500,
              maxRetryDelayMs: 4_000,
            },
            errorRetry: {
              maxNumRetry: 2,
              retryDelayMs: 1_000,
              maxRetryDelayMs: 8_000,
            },
          },
        },
        playlistLoadPolicy: {
          default: {
            maxTimeToFirstByteMs: HLS_PLAYLIST_TTFB_TIMEOUT_MS,
            maxLoadTimeMs: 25_000,
            timeoutRetry: {
              maxNumRetry: 2,
              retryDelayMs: 500,
              maxRetryDelayMs: 4_000,
            },
            errorRetry: {
              maxNumRetry: 3,
              retryDelayMs: 1_000,
              maxRetryDelayMs: 8_000,
            },
          },
        },
        fragLoadPolicy: {
          default: {
            maxTimeToFirstByteMs: 15_000,
            maxLoadTimeMs: 30_000,
            timeoutRetry: {
              maxNumRetry: 2,
              retryDelayMs: 500,
              maxRetryDelayMs: 4_000,
            },
            errorRetry: {
              maxNumRetry: 3,
              retryDelayMs: 1_000,
              maxRetryDelayMs: 8_000,
            },
          },
        },
        xhrSetup: (request) => {
          for (const [key, value] of Object.entries(
            JSON.parse(headerSignature) as Record<string, string>,
          )) {
            request.setRequestHeader(key, value);
          }
        },
      });
      hlsRef.current = hls;
      hls.attachMedia(video);
      const bufferedAheadSeconds = () => {
        try {
          for (let index = 0; index < video.buffered.length; index += 1) {
            if (
              video.buffered.start(index) <= video.currentTime &&
              video.buffered.end(index) >= video.currentTime
            ) {
              return video.buffered.end(index) - video.currentTime;
            }
          }
        } catch {
          return 0;
        }
        return 0;
      };
      const alignInitialPlayheadToBuffer = () => {
        if (initialBufferedAlignmentDone || video.buffered.length === 0) return;
        const currentTime = video.currentTime;
        const ranges = Array.from({ length: video.buffered.length }, (_, index) => ({
          start: video.buffered.start(index),
          end: video.buffered.end(index),
        }));
        const correctedTime = getInitialBufferedPlayheadCorrection(currentTime, ranges);
        if (correctedTime === undefined) {
          initialBufferedAlignmentDone = true;
          return;
        }
        // Some source files retain a positive packet timestamp after Remux
        // starts a managed-HLS generation. Chromium can then leave autoplay at
        // decoder time zero even though the first appended range starts later;
        // a manual seek works because it moves the playhead into that range.
        // Make that safe startup correction once without overriding a pause.
        video.currentTime = correctedTime;
        initialBufferedAlignmentDone = true;
        console.debug("[player] aligned initial HLS playhead", {
          from: currentTime,
          to: video.currentTime,
        });
        if (!pausedIntentRef.current) {
          void video.play().then(() => {
            playBlockedRef.current = false;
          }).catch(handlePlayRejection);
        }
      };
      const executeRecoveryDecision = (
        decision: HlsRecoveryDecision,
        details: string,
        httpStatus?: number,
      ) => {
        if (!hls || terminalErrorSent || decision.action === "ignore") return;
        if (decision.action === "restart-load") {
          stallRecoveryAttempted = true;
          onBuffering(true);
          console.debug("[player] restarting HLS load after forward-buffer stall", {
            currentTime: video.currentTime.toFixed(1),
          });
          hls.startLoad(Math.max(0, video.currentTime));
          return;
        }
        if (decision.action === "recover-media") {
          mediaRecoveryAttempted = true;
          if (decision.showBuffering) onBuffering(true);
          hls.recoverMediaError();
          return;
        }
        terminalErrorSent = true;
        onErrorRef.current(decision.message, {
          kind: decision.kind,
          fatal: true,
          details,
          httpStatus,
        });
      };
      const resumeDeferredRecovery = () => {
        if (
          !deferredFailure ||
          video.paused ||
          video.ended ||
          bufferedAheadSeconds() > 3
        ) {
          return;
        }
        const pending = deferredFailure;
        deferredFailure = null;
        executeRecoveryDecision(
          pending.decision,
          pending.details,
          pending.httpStatus,
        );
      };
      video.addEventListener("play", resumeDeferredRecovery);
      video.addEventListener("timeupdate", resumeDeferredRecovery);
      cleanupDeferredRecovery = () => {
        video.removeEventListener("play", resumeDeferredRecovery);
        video.removeEventListener("timeupdate", resumeDeferredRecovery);
      };
      hls.on(Hls.Events.MEDIA_ATTACHING, () => {
        for (let i = 0; i < video.textTracks.length; i++) {
          const t = video.textTracks[i];
          if (t === managedTrackRef.current?.track) continue;
          if (t && t.cues) while (t.cues.length) t.removeCue(t.cues[0]);
        }
      });
      hls.on(Hls.Events.MANIFEST_LOADING, () => {
        for (let i = 0; i < video.textTracks.length; i++) {
          const t = video.textTracks[i];
          if (t === managedTrackRef.current?.track) continue;
          if (t && t.cues) while (t.cues.length) t.removeCue(t.cues[0]);
        }
      });
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls?.loadSource(uri));
      hls.on(Hls.Events.FRAG_LOADED, (_, data) => {
        const stats = (data as typeof data & {
          stats?: {
            loaded?: number;
            loading?: { start?: number; end?: number };
          };
        }).stats;
        const loadedBytes = Number(stats?.loaded);
        const loadStart = Number(stats?.loading?.start);
        const loadEnd = Number(stats?.loading?.end);
        if (loadedBytes > 0 && loadEnd >= loadStart && loadStart > 0) {
          onTransportMetricRef.current?.({
            kind: "fragment",
            bytes: loadedBytes,
            loadMs: loadEnd - loadStart,
            mediaDurationSeconds: Number.isFinite(data.frag.duration)
              ? data.frag.duration
              : undefined,
          });
        }
        if (firstFragStartRef.current === null) {
          firstFragStartRef.current = data.frag.start;
          setFragEpoch((value) => value + 1);
          console.debug("[player] first fragment", {
            start: data.frag.start,
            sn: data.frag.sn,
            streamStartSec: streamStartSeconds,
            offsetMs: -((streamStartSeconds ?? 0) + data.frag.start) * 1000,
          });
        }
      });
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        alignInitialPlayheadToBuffer();
        onBuffering(false);
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal || (data.details as string) === "bufferStalledError") {
          const buf: string[] = [];
          for (let i = 0; i < video.buffered.length; i++) {
            buf.push(video.buffered.start(i).toFixed(1) + "–" + video.buffered.end(i).toFixed(1));
          }
          console.debug("[player] hls issue", {
            type: data.type,
            details: data.details,
            currentTime: video.currentTime.toFixed(1),
            paused: video.paused,
            buffer: buf.join(", ") || "empty",
            readyState: video.readyState,
          });
        }
        if (!hls || terminalErrorSent) return;
        const bufferedAhead = bufferedAheadSeconds();
        const responseStatus =
          data.response?.code ||
          (data.networkDetails instanceof XMLHttpRequest
            ? data.networkDetails.status
            : undefined);
        const remuxHlsState = data.networkDetails instanceof XMLHttpRequest
          ? data.networkDetails.getResponseHeader("X-Remux-Hls-State") ?? undefined
          : undefined;
        const failureDetails = remuxHlsState
          ? `${String(data.details)} (${remuxHlsState})`
          : String(data.details);
        if (responseStatus || remuxHlsState) {
          console.debug("[player] HLS response failure", {
            status: responseStatus,
            remuxState: remuxHlsState,
            details: data.details,
          });
        }
        const decision = decideHlsRecovery({
          fatal: data.fatal,
          type: data.type,
          details: failureDetails,
          httpStatus: responseStatus,
          bufferedAheadSeconds: bufferedAhead,
          paused: video.paused,
          ended: video.ended,
        }, { mediaRecoveryAttempted, stallRecoveryAttempted });
        if (decision.action === "ignore") {
          if (decision.showBuffering) onBuffering(true);
          return;
        }
        // A destructive recovery detaches the MediaSource and discards bytes
        // the user has already buffered. While paused, preserve the current
        // attachment. Resume from those bytes first, then recover only when the
        // forward buffer is nearly exhausted.
        if (shouldDeferHlsFailure({
          paused: video.paused,
          ended: video.ended,
          hasBufferedMedia: video.buffered.length > 0,
        })) {
          deferredFailure = {
            decision,
            details: failureDetails,
            httpStatus: responseStatus,
          };
          onBuffering(false);
          return;
        }
        executeRecoveryDecision(
          decision,
          failureDetails,
          responseStatus,
        );
      });
    } else {
      video.src = uri;
    }
    return () => {
      metadataReadyRef.current = false;
      cleanupDeferredRecovery();
      hls?.stopLoad();
      hls?.detachMedia();
      hls?.destroy();
      hlsRef.current = null;
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, [headerSignature, livePlayback, uri]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = muted;
    video.volume = Number.isFinite(volume) ? Math.max(0, Math.min(1, volume)) : 1;
    video.playbackRate = Number.isFinite(rate) && rate > 0 ? rate : 1;
    if (paused) video.pause();
    else void video.play().then(() => {
      playBlockedRef.current = false;
    }).catch((error) => {
      // A rejected play() promise is authoritative even before metadata is
      // ready only when the browser reports a policy denial. Preparation-time
      // source/abort failures are retried once metadata establishes a decoder.
      handlePlayRejection(error);
    });
  }, [muted, paused, rate, volume, uri]);

  // ---- subtitle rendering ----
  const activeTrack: SubtitleTrackDescriptor | null =
    subtitleTracks && activeSubtitleIndex != null && activeSubtitleIndex >= 0
      ? subtitleTracks[activeSubtitleIndex] ?? null
      : null;
  const subtitleStreamStartDependency = activeTrack?.rendererKind ? streamStartSeconds : 0;
  const synchronizeManagedVttCueOffset = () => {
    const track = managedTrackRef.current?.track;
    if (!track?.cues?.length) return;
    const nextOffsetMs = getVttCueOffsetMs(streamStartSecondsRef.current, firstFragStartRef.current ?? 0);
    const nextOffsetSeconds = nextOffsetMs / 1000;
    if (!Number.isFinite(nextOffsetSeconds)) return;
    for (let cueIndex = 0; cueIndex < track.cues.length; cueIndex += 1) {
      const cue = track.cues[cueIndex];
      if (!cue) continue;
      let sourceTimes = managedVttCueTimesRef.current.get(cue);
      if (!sourceTimes) {
        sourceTimes = { start: cue.startTime, end: cue.endTime };
        managedVttCueTimesRef.current.set(cue, sourceTimes);
      }
      const shiftedStart = sourceTimes.start + nextOffsetSeconds;
      const shiftedEnd = sourceTimes.end + nextOffsetSeconds;
      if (shiftedEnd <= 0) {
        // A zero-length cue at t=0 is considered active by Chromium. Park
        // expired cues outside any real media duration while retaining their
        // source times in the WeakMap so a backward window can restore them.
        cue.startTime = Number.MAX_SAFE_INTEGER;
        cue.endTime = Number.MAX_SAFE_INTEGER;
      } else {
        cue.startTime = Math.max(0, shiftedStart);
        cue.endTime = Math.max(cue.startTime, shiftedEnd);
      }
    }
    managedVttOffsetMsRef.current = nextOffsetMs;
  };

  const clearSubtitleDisplay = (video: HTMLVideoElement) => {
    for (let i = 0; i < video.textTracks.length; i++) {
      const textTrack = video.textTracks[i];
      if (!textTrack) continue;
      if (textTrack.mode !== "disabled") textTrack.mode = "disabled";
      if (!textTrack.cues) continue;
      try {
        for (let ci = textTrack.cues.length - 1; ci >= 0; ci--) {
          const cue = textTrack.cues[ci];
          if (cue) textTrack.removeCue(cue);
        }
      } catch {
        // Some engines expose a live cue list without removeCue support.
      }
    }
    const parent = video.parentElement;
    if (!parent) return;
    const overlaySelector = ".JASSUB, .jellyflix-subtitle-overlay canvas";
    parent.querySelectorAll(overlaySelector).forEach((node) => {
      node.remove();
    });
    const allTrackElements = parent.querySelectorAll("track");
    allTrackElements.forEach((track) => {
      if (track.getAttribute("kind") === "subtitles") track.remove();
    });
  };
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Tearing down the previous subtitle identity must run for every
    // selection change — including Off (no active track) and tracks with no
    // fetchable candidates — otherwise a JASSUB/libbitsub canvas or a showing
    // TextTrack keeps rendering after the user turns subtitles off.
    const teardownSubtitles = () => {
      clearSubtitleDisplay(video);
      activeSubSigRef.current = "";
      if (rendererRef.current) {
        const handle = rendererRef.current;
        rendererRef.current = null;
        void destroyRenderer(handle);
      }
      if (managedTrackRef.current && video.contains(managedTrackRef.current)) {
        video.removeChild(managedTrackRef.current);
      }
      managedTrackRef.current = null;
      managedVttOffsetMsRef.current = 0;
      managedVttCueTimesRef.current = new WeakMap();
      if (managedBlobUrlRef.current) {
        URL.revokeObjectURL(managedBlobUrlRef.current);
        managedBlobUrlRef.current = null;
      }
    };
    teardownSubtitles();

    if (!activeTrack) { return; }
    if (!activeTrack.srcCandidates.length) { return; }
    const sig = `${activeTrack.sourceKey}:${activeTrack.rendererKind ?? "vtt"}`;
    if (activeSubSigRef.current === sig) return;
    activeSubSigRef.current = sig;

    const abortCtrl = new AbortController();
    subtitleAbortRef.current = abortCtrl;
    let disposed = false;
    const stillActive = () => !disposed && activeSubSigRef.current === sig;
    const loadSubtitlePayload = async (
      url: string,
      forceRefresh = false,
    ): Promise<{ payload: Blob; partial: boolean } | null> => {
      const cached = forceRefresh ? undefined : subtitlePayloadCacheRef.current.get(url);
      if (cached) {
        // Refresh insertion order so the small player-session cache behaves as
        // LRU while source and track menus are exercised.
        subtitlePayloadCacheRef.current.delete(url);
        subtitlePayloadCacheRef.current.set(url, cached);
        console.debug("[player] subtitle payload cache hit", { bytes: cached.size });
        return { payload: cached, partial: false };
      }
      const startedAt = performance.now();
      const response = await fetchSubtitleCandidate(url, abortCtrl.signal);
      if (!response.ok) return null;
      const payload = await response.blob();
      if (!stillActive()) return null;
      const partial = response.headers.get("x-remux-subtitle-partial") === "true";
      console.debug("[player] subtitle payload loaded", {
        bytes: payload.size,
        partial,
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      if (!partial) {
        subtitlePayloadCacheRef.current.set(url, payload);
        while (subtitlePayloadCacheRef.current.size > 4) {
          const oldest = subtitlePayloadCacheRef.current.keys().next().value as string | undefined;
          if (!oldest) break;
          subtitlePayloadCacheRef.current.delete(oldest);
        }
      }
      return { payload, partial };
    };

    const doAttachVttTrack = (blobUrl: string) => {
      if (!stillActive() || managedTrackRef.current) return;
      for (let i = 0; i < video.textTracks.length; i++) {
        const t = video.textTracks[i];
        if (t && t.mode !== "disabled") t.mode = "disabled";
      }
      const trackEl = document.createElement("track");
      trackEl.kind = "subtitles";
      trackEl.src = blobUrl;
      trackEl.srclang = activeTrack.srclang;
      trackEl.label = activeTrack.label;
      trackEl.default = false;
      video.appendChild(trackEl);
      managedTrackRef.current = trackEl;
      managedBlobUrlRef.current = blobUrl;

      const enforceShowing = () => {
        if (!stillActive() || !trackEl.track) return;
        synchronizeManagedVttCueOffset();
        const wasShowing = trackEl.track.mode === "showing";
        if (wasShowing) trackEl.track.mode = "hidden";
        for (let ci = 0; ci < (trackEl.track.cues?.length || 0); ci++) {
          const cue = trackEl.track.cues?.[ci] as VTTCue | null;
          if (cue) cue.line = vttBottomOffset > 0 ? -Math.ceil(vttBottomOffset / 24) : "auto";
        }
        trackEl.track.mode = "showing";
      };
      trackEl.addEventListener("load", enforceShowing);
      enforceShowing();
    };

    const attachVttTrack = (blobUrl: string) => {
      doAttachVttTrack(blobUrl);
      const track = managedTrackRef.current?.track;
      if (track) track.mode = "hidden";
      if (video.videoWidth > 0) {
        if (track) track.mode = "showing";
        return;
      }
      let revealed = false;
      const reveal = () => {
        if (!stillActive() || revealed) return;
        revealed = true;
        video.removeEventListener("playing", reveal);
        const t = managedTrackRef.current?.track;
        if (t && stillActive()) t.mode = "showing";
      };
      video.addEventListener("playing", reveal);
      setTimeout(() => {
        if (!stillActive() || revealed) return;
        revealed = true;
        video.removeEventListener("playing", reveal);
        const t = managedTrackRef.current?.track;
        if (t && stillActive()) t.mode = "showing";
      }, 500);
    };

    const loadVttSubtitle = async () => {
      // Skip VTT for obvious non-text formats — let libbitsub/JASSUB handle them
      const candidates = activeTrack.srcCandidates.filter(
        (c) => c.mode === "webvtt" && !/\.(sup|pgs|ass|ssa)($|\?)/i.test(c.url)
      );
      if (candidates.length === 0) return;
      for (const candidate of candidates) {
        if (!stillActive()) return;
        try {
          console.debug("[player] vtt fetching", { source: candidate.source });
          let loaded = await loadSubtitlePayload(candidate.url);
          if (!loaded) continue;
          let installedSize = 0;
          const install = async (payload: Blob) => {
            const text = await payload.text();
            if (!stillActive()) return;
            const cueOffsetMs = getVttCueOffsetMs(streamStartSecondsRef.current, firstFragStartRef.current ?? 0);
            // Keep source timestamps intact in the parsed cues. The managed cue
            // map derives every HLS-window offset from those immutable values,
            // so clamping cues before zero can never corrupt a later seek.
            const shifted = applySubtitleOffsetToWebVtt(text, 0);
            const blob = new Blob([shifted], { type: "text/vtt;charset=utf-8" });
            const blobUrl = URL.createObjectURL(blob);
            if (managedTrackRef.current && video.contains(managedTrackRef.current)) {
              video.removeChild(managedTrackRef.current);
              managedTrackRef.current = null;
            }
            if (managedBlobUrlRef.current) {
              URL.revokeObjectURL(managedBlobUrlRef.current);
              managedBlobUrlRef.current = null;
            }
            managedVttCueTimesRef.current = new WeakMap();
            managedVttOffsetMsRef.current = cueOffsetMs;
            attachVttTrack(blobUrl);
            installedSize = payload.size;
          };
          await install(loaded.payload);
          while (loaded.partial && stillActive()) {
            await new Promise((resolve) => window.setTimeout(resolve, 15_000));
            if (!stillActive()) return;
            const refreshed = await loadSubtitlePayload(candidate.url, true);
            if (!refreshed) return;
            if (refreshed.payload.size > installedSize || !refreshed.partial) {
              await install(refreshed.payload);
            }
            loaded = refreshed;
          }
          return;
        } catch (err) {
          if (abortCtrl.signal.aborted || !stillActive()) return;
        }
      }
    };

    const timeOffsetSec = getSubtitleTimeOffsetSec(streamStartSeconds);

    const loadJassubSubtitle = async () => {
      const JASSUB = (await import("jassub")).default;
      if (!stillActive()) return;
      // Try binary ASS candidates first, then webvtt candidates with .ass format
      console.debug("[player] jassub binary candidates", activeTrack.srcCandidates.map((candidate) => ({
        mode: candidate.mode,
        source: candidate.source,
      })));
      let candidates = activeTrack.srcCandidates.filter((c) => c.mode === "binary");
      if (candidates.length === 0) {
        // Server misreports ASS as SRT — try webvtt URLs with .ass extension
        candidates = activeTrack.srcCandidates
          .filter((c) => c.mode === "webvtt")
          .map((c) => ({ ...c, url: c.url.replace(/Stream\.vtt/i, "Stream.ass") }));
      }
      if (candidates.length === 0) { void loadVttSubtitle(); return; }
      for (const candidate of candidates) {
        if (!stillActive()) return;
        try {
          console.debug("[player] jassub fetching", { source: candidate.source });
          const loaded = await loadSubtitlePayload(candidate.url);
          if (!loaded) continue;
          const text = await loaded.payload.text();
          if (!stillActive()) return;
          if (!text.trimStart().startsWith("[Script Info]")) continue;
          const renderer = new JASSUB({
            video,
            subContent: text,
            timeOffset: timeOffsetSec,
            // Let JASSUB register its bundled Liberation Sans fallback. Its
            // availableFonts values must be font URLs/bytes, not family names.
            // Browser local-font discovery is both unnecessary and unsupported
            // by Firefox's Permissions API.
            queryFonts: false,
          });
          await renderer.ready;
          if (!stillActive()) { await renderer.destroy(); return; }
          const renderJassubAtCurrentTime = () => {
            if (!stillActive()) return;
            void renderer.manualRender({
              expectedDisplayTime: performance.now(),
              mediaTime: video.currentTime,
              width: video.videoWidth || video.clientWidth,
              height: video.videoHeight || video.clientHeight,
            }, true).catch(() => undefined);
          };
          // requestVideoFrameCallback normally follows seeks while playing,
          // but a paused seek is browser-dependent. Make the new media clock
          // authoritative as soon as the decoder acknowledges the seek.
          video.addEventListener("seeked", renderJassubAtCurrentTime);
          // Force a redraw so the first subtitle frame appears immediately.
          // Without this, the WebGL canvas stays blank until the next timeupdate.
          void renderer.resize(true).catch(() => undefined);
          // Ensure the JASSUB canvas is positioned above the video.
          const canvas = video.parentElement?.querySelector('.JASSUB') as HTMLElement | null;
          if (canvas) {
            canvas.style.position = 'absolute';
            canvas.style.top = '0';
            canvas.style.left = '0';
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            canvas.style.pointerEvents = 'none';
            canvas.style.zIndex = '10';
            canvas.style.background = 'transparent';
            // Ensure the video parent can anchor the absolute canvas
            const parent = video.parentElement;
            if (parent) {
              const pos = getComputedStyle(parent).position;
              if (pos === 'static') parent.style.position = 'relative';
            }
          }
          rendererRef.current = {
            kind: "jassub",
            destroy: async () => {
              video.removeEventListener("seeked", renderJassubAtCurrentTime);
              await renderer.destroy();
            }
          };
          return;
        } catch (err) {
          console.debug("[player] jassub candidate failed", { error: String(err) });
          if (abortCtrl.signal.aborted || !stillActive()) return;
        }
      }
      void loadVttSubtitle();
    };

    const loadLibbitsubSubtitle = async () => {
      const { createAutoSubtitleRenderer } = await import("libbitsub");
      const candidates = activeTrack.srcCandidates.filter((c) => c.mode === "binary");
      if (candidates.length === 0) { void loadVttSubtitle(); return; }
      for (const candidate of candidates) {
        if (!stillActive()) return;
        let subtitleBlobUrl = "";
        try {
          const loaded = await loadSubtitlePayload(candidate.url);
          if (!loaded) continue;
          subtitleBlobUrl = URL.createObjectURL(loaded.payload);
          if (!stillActive()) {
            URL.revokeObjectURL(subtitleBlobUrl);
            return;
          }
          const renderer = createAutoSubtitleRenderer({
            video,
            subUrl: subtitleBlobUrl,
            // libbitsub's detector intentionally treats the URL as a file
            // hint, but a signed Jellyfin URL ends in `.sup?ApiKey=...`.
            // Give it the query-free filename so PGS is classified before its
            // asynchronous fetch begins.
            fileName: getSubtitleCandidateFileName(candidate.url),
            timeOffset: timeOffsetSec,
            onError: (error) => {
              console.debug("[player] libbitsub renderer failed", {
                error: String(error),
              });
            },
          });
          rendererRef.current = {
            kind: "libbitsub",
            destroy: async () => {
              try { renderer.dispose(); } catch { /* ignore */ }
              URL.revokeObjectURL(subtitleBlobUrl);
            },
          };
          return;
        } catch (err) {
          if (subtitleBlobUrl) URL.revokeObjectURL(subtitleBlobUrl);
          console.debug("[player] libbitsub candidate failed", { error: String(err) });
          if (abortCtrl.signal.aborted || !stillActive()) return;
        }
      }
      void loadVttSubtitle();
    };

    const kind = activeTrack.rendererKind;
    if (kind === "jassub") void loadJassubSubtitle();
    else if (kind === "libbitsub") void loadLibbitsubSubtitle();
    else void loadVttSubtitle();

    return () => {
      disposed = true;
      abortCtrl.abort();
      if (subtitleAbortRef.current === abortCtrl) subtitleAbortRef.current = null;
      if (activeSubSigRef.current === sig) activeSubSigRef.current = "";
      // Surface unmount must release the renderer too; the top-of-effect
      // teardown only runs when a new selection arrives.
      teardownSubtitles();
    };
  }, [activeTrack?.sourceKey, subtitleStreamStartDependency]);

  // The first fragment timestamp refines the mapping from absolute subtitle
  // time to the local HLS decoder clock. Retiming native cues in place keeps a
  // slow SRT/VTT extraction alive instead of aborting and downloading it again.
  useEffect(() => {
    synchronizeManagedVttCueOffset();
  }, [fragEpoch, streamStartSeconds]);

  // ---- subtitle position + cue adjustment ----
  const vttBottomOffset = controlsVisible ? Math.max(0, controlsHeight || 68) : 0;

  useEffect(() => {
    const styleId = "jellyflix-vtt-position";
    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `
      video::-webkit-media-text-track-display {
        transform: translateY(-${vttBottomOffset}px) !important;
        transition: transform 0.18s ease-out;
      }
      video::cue {
        visibility: visible !important;
        background: transparent !important;
        background-color: transparent !important;
        text-shadow: 1px 1px 2px rgba(0,0,0,0.85), -1px -1px 2px rgba(0,0,0,0.85), 1px -1px 2px rgba(0,0,0,0.85), -1px 1px 2px rgba(0,0,0,0.85), 0 1px 4px rgba(0,0,0,0.7);
      }
      .JASSUB, .jellyflix-subtitle-overlay canvas {
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        z-index: 10 !important;
        background: transparent !important;
        pointer-events: none !important;
      }
    `;
    return () => { if (styleEl) styleEl.textContent = ""; };
  }, [vttBottomOffset]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const adjustCueLines = () => {
      for (let ti = 0; ti < video.textTracks.length; ti++) {
        const track = video.textTracks[ti];
        if (!track || track.kind !== "subtitles" || track.mode !== "showing") continue;
        if (!track.cues || track.cues.length === 0) continue;
        const targetLine: number | "auto" = vttBottomOffset > 0 ? -Math.ceil(vttBottomOffset / 24) : "auto";
        const wasShowing = track.mode === "showing";
        if (wasShowing) track.mode = "hidden";
        for (let ci = 0; ci < track.cues.length; ci++) {
          const cue = track.cues[ci] as VTTCue | null;
          if (cue) cue.line = targetLine;
        }
        if (wasShowing) track.mode = "showing";
      }
    };
    adjustCueLines();
    const onAddTrack = () => adjustCueLines();
    const handleTrackLoad = () => adjustCueLines();
    video.textTracks.addEventListener("addtrack", onAddTrack);
    for (let i = 0; i < video.textTracks.length; i++) {
      video.textTracks[i].addEventListener("load", handleTrackLoad);
    }
    return () => {
      video.textTracks.removeEventListener("addtrack", onAddTrack);
      for (let i = 0; i < video.textTracks.length; i++) {
        video.textTracks[i].removeEventListener("load", handleTrackLoad);
      }
    };
  }, [vttBottomOffset]);

  return (
    <View style={styles.surface}>
      {unstable_createElement("video", {
        ref: (node: WebVideo | null) => {
          videoRef.current = node;
        },
        poster: posterUri,
        playsInline: true,
        style: [
          styles.video,
          { width: fittedWidth, height: fittedHeight },
        ],
        onLoadedMetadata: () => {
          const video = videoRef.current;
          if (video) {
            metadataReadyRef.current = true;
            if (video.videoWidth > 0 && video.videoHeight > 0) {
              setAspectRatio(video.videoWidth / video.videoHeight);
            }
            onReady(Number.isFinite(video.duration) ? video.duration : 0);
            if (!paused && !playBlockedRef.current) {
              void video.play().then(() => {
                playBlockedRef.current = false;
              }).catch(handlePlayRejection);
            }
          }
        },
        onTimeUpdate: () => {
          const video = videoRef.current;
          if (video) {
            if (!video.paused && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
              onBuffering(false);
            }
            const bufferedUntil = video.buffered.length
              ? video.buffered.end(video.buffered.length - 1)
              : video.currentTime;
            onProgress(video.currentTime, bufferedUntil);
          }
        },
        onSeeked: () => {
          const video = videoRef.current;
          if (video) onProgress(video.currentTime);
        },
        onWaiting: () => {
          const video = videoRef.current;
          onBuffering(Boolean(video && !video.paused && !video.ended));
        },
        onCanPlay: () => onBuffering(false),
        onPlaying: () => {
          playBlockedRef.current = false;
          onBuffering(false);
          onPlayingChange(true);
        },
        onPause: () => {
          onBuffering(false);
          if (metadataReadyRef.current) onPlayingChange(false);
        },
        onEnded,
        onError: () => onError("The browser could not play this source.", {
          kind: "media",
          fatal: true,
        }),
      })}
    </View>
  );
});

const styles = StyleSheet.create({
  surface: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#000",
  },
  video: {
    backgroundColor: "#000",
  },
});
